import { useContext, useEffect, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import { Alert } from "antd";
import type { TableProps } from "antd";
import type { Dayjs } from "dayjs";

import AppContext from "../../context/AppContext";
import { DATE_RANGE, KPI_CARD_BACKGROUND } from "../../utils/Constants";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getAdminNow } from "../../utils/dateService";
import {
  Button,
  DataTable,
  DatePicker,
  KpiCard as KpiCardJs,
  Select,
} from "../shared";

import styles from "./TopUsers.module.css";

// `KpiCard` is implemented in JS — re-cast through `ComponentType<...>` so
// TS strict-mode accepts the props we actually pass without surfacing the
// JS file's inferred prop types. Same pattern as `OverviewV2.tsx`.
const KpiCard = KpiCardJs as ComponentType<{
  title: string;
  titleTooltip?: ReactNode;
  value: number;
  percentage?: number;
  rate?: boolean;
  maximumFractionDigits?: number;
  percentageMaximumFractionDigits?: number;
  backgroundColor?: string;
  isApiRunning?: boolean;
  hidePercentage?: boolean;
  currencySymbol?: string;
  /**
   * URL of an image rendered to the left of the value as the currency
   * symbol (typically the primary-currency icon, e.g. `$`). When set,
   * `KpiCard` skips the default `AppStrings.COUNTRY_CURRENCY_SYMBOL`
   * text prefix and shows this image instead — same toggle used by
   * `WithdrawalsManualActions`.
   */
  currencySourceImage?: string;
}>;

// ─── Shared types (re-exported for the per-product wrappers) ─────────────────

type AppContextValue = {
  setActiveSidebarTab?: (parent: string, child: string) => void;
};

export type TopUsersViewOption = {
  /** Stable id, mirrored back to the wrapper through `onViewChange`. */
  id: string;
  label: string;
};

export type TopUsersSelectOption<T extends string | number = string> = {
  value: T;
  label: string;
};

/**
 * Per-filter value held inside the draft / applied filter map.
 *
 * - Single-select filters store a scalar (`string` for codes, `number` for
 *   limits, …) or `undefined` when cleared. Untouched single-selects also
 *   surface as `undefined` (= "no value yet").
 * - Multi-select filters (`TopUsersFilterField.mode === "multiple"`) store
 *   an array of scalars. The empty array is **both** the cleared state and
 *   the initial state — multi-select values never surface as `undefined`
 *   to consumers, even when the wrapper omits `defaultValue`. BE encoders
 *   can therefore rely on a single "no filter" sentinel (`[].length === 0`)
 *   instead of also having to handle `undefined`.
 *
 * The shared component does not validate that the value shape matches the
 * filter's `mode` — that's the wrapper's contract via `defaultValue` and
 * the `Select` it renders — but it does seed the draft accordingly (see
 * `draftFilters` init below).
 */
export type TopUsersFilterValue =
  | string
  | number
  | ReadonlyArray<string | number>
  | undefined;

export type TopUsersFilterField = {
  /** Stable key — also the key under which the value is stored in `onApply`. */
  id: string;
  placeholder: string;
  options: ReadonlyArray<TopUsersSelectOption<string | number>>;
  allowClear?: boolean;
  /**
   * Selection mode. Omit for a single-value select (`value` is a scalar).
   * Pass `"multiple"` to render a multi-select (`value` is an array — the
   * wrapper's `onApply` callback receives the array verbatim, so the BE
   * encoder should switch from `?key[eq]=<v>` to `?key[in]=<v>,<v>`).
   */
  mode?: "multiple";
  /**
   * Initial draft value. Must match the shape implied by `mode` — a
   * scalar for single-select, an array for multi-select.
   */
  defaultValue?: string | number | ReadonlyArray<string | number>;
  /** Override the auto-generated `data-testid`. */
  testId?: string;
};

export type TopUsersKpiTile = {
  /** Stable key for the React list. */
  key: string;
  title: string;
  /**
   * Optional info-icon tooltip rendered to the right of the title (inside
   * `KpiCard`, via its `titleTooltip` prop). Wrappers use this to surface
   * "what does this metric *actually* mean?" copy without crowding the
   * tile body — see `Sportsbook/TopUsers.tsx`'s `buildPercentChangeTooltip`.
   */
  titleTooltip?: ReactNode;
  value: number;
  percentage?: number;
  /** When `true`, the KPI tile renders the value with a trailing "%". */
  rate?: boolean;
  maximumFractionDigits?: number;
  percentageMaximumFractionDigits?: number;
  backgroundColor?: string;
  hidePercentage?: boolean;
  /** Text symbol used for fiat values. Omit for rates and counts. */
  currencySymbol?: string;
  /**
   * URL of the primary-currency icon to render to the left of the
   * value (replaces `KpiCard`'s default text symbol). Currency tiles
   * (e.g. Sportsbook turnover / P&L) set this; rate tiles leave it
   * undefined so the trailing `%` is the only unit indicator.
   */
  currencySourceImage?: string;
};

export type TopUsersColumn<TRow = Record<string, unknown>> = {
  title: ReactNode;
  dataIndex: string;
  key: string;
  width?: number | string;
  sorter?: (a: TRow, b: TRow) => number;
  render?: (value: unknown, record: TRow, index: number) => ReactNode;
};

export type TopUsersAppliedParams = {
  dates: [Dayjs, Dayjs];
  view?: string;
  /** Includes `limit` plus every entry from `filters`. */
  filters: Record<string, TopUsersFilterValue>;
};

export type TopUsersProps<TRow = Record<string, unknown>> = {
  /**
   * Sider parent tab to activate (a `SIDER_TABS` value).
   */
  product: string;
  /** Heading text. Defaults to `AppStrings.TOP_USERS` ("Top Users"). */
  title?: string;
  /** Hide the in-page heading when the authenticated layout owns it. */
  hideTitle?: boolean;

  /**
   * Show the date range picker in the header. Defaults to `false` so the
   * placeholder pages (Casino / Poker today) stay minimal until the BE
   * contract ships.
   */
  showDatePicker?: boolean;
  /** Initial value of the date picker. Defaults to current month → today. */
  initialDates?: [Dayjs, Dayjs];
  /** Notified whenever the user picks a new date range. */
  onDatesChange?: (dates: [Dayjs, Dayjs]) => void;

  /**
   * View tab strip rendered above the filter row. The first entry is
   * selected by default (unless `activeView` is provided). Omit to hide
   * the tab strip entirely.
   */
  views?: ReadonlyArray<TopUsersViewOption>;
  /** Controlled active view (otherwise the component owns the state). */
  activeView?: string;
  /** Initial active view in uncontrolled mode. Defaults to `views[0].id`. */
  defaultView?: string;
  /** Notified whenever the user clicks a different tab. */
  onViewChange?: (id: string) => void;

  /**
   * Extra `Select` filters rendered next to the limit select. Omit (or
   * pass an empty array) to hide the entire filter row.
   */
  filters?: ReadonlyArray<TopUsersFilterField>;
  /**
   * Options for the leading "limit" select. Defaults to 10/25/50/100. Pass
   * `null` to hide the limit select (a wrapper that doesn't paginate
   * server-side can do that).
   */
  limitOptions?: ReadonlyArray<TopUsersSelectOption<number>> | null;
  /** Initial value of the limit select. */
  defaultLimit?: number;
  /**
   * Notified when the user clicks Apply. Receives the current date range,
   * the active view, and the committed filter values (including `limit`).
   * The wrapper typically uses this to refetch.
   */
  onApply?: (params: TopUsersAppliedParams) => void;
  /**
   * Notified when the user clicks Clear Filters. The shared component
   * has already reset its internal drafts back to their initial values
   * by the time this fires — the wrapper should mirror that on its own
   * applied/committed filters so the table re-shows the unfiltered slice.
   *
   * Mirrors the `UtmReports` pattern (`handleClearFilters` there resets
   * both drafts and applied state in one go) so the user gets a single
   * "actually undo my filters" affordance.
   *
   * The date range and the active view tab are intentionally left
   * untouched — they're separate primary controls, not filters.
   */
  onClear?: () => void;
  /**
   * Show the Clear Filters button to the left of Apply. The wrapper is
   * the source of truth here — visibility tracks whether the wrapper
   * actually has non-default applied filters, so the button stays
   * hidden on first paint (nothing to clear). Mirrors
   * `UtmReports.hasAppliedFilters`.
   */
  showClear?: boolean;

  /** KPI tiles. Omit (or pass an empty array) to hide the KPI grid. */
  kpis?: ReadonlyArray<TopUsersKpiTile>;
  /**
   * When `true`, every KPI tile renders its skeleton state (via
   * `KpiCard.isApiRunning`). Kept separate from `loading` so the KPI
   * row and the table can show independent loading states when they're
   * powered by different queries (the common case — KPIs come from a
   * dedicated `/kpis` endpoint).
   */
  kpisLoading?: boolean;

  /** Table columns. Omit (or pass an empty array) to hide the table. */
  columns?: ReadonlyArray<TopUsersColumn<TRow>>;
  /** Table rows. */
  rows?: ReadonlyArray<TRow>;
  /** Key field in each row. Defaults to `"key"`. */
  rowKey?: string;
  /** Forwarded to `DataTable` to render a loading state. */
  loading?: boolean;
  /**
   * Forwarded verbatim to the underlying antd `<Table>` (via the
   * shared `DataTable`). Lets a per-product wrapper opt into a custom
   * skin without the shared component knowing about it — Sportsbook,
   * for example, applies `"reporting-summary-table"` so its rows
   * match the Reporting Summary look exactly.
   */
  tableClassName?: string;
  /**
   * Forwarded to antd `<Table>` for per-row className resolution
   * (typically zebra striping, e.g. `index % 2 === 1 ? "alt" : ""`).
   * Same signature as antd to keep parity.
   */
  rowClassName?: TableProps<TRow>["rowClassName"];
  /**
   * When set, renders an `antd <Alert type="error">` banner above the
   * toolbar so the operator can tell "request failed" apart from "no
   * data returned" — without this, a 4xx/5xx from `/top-users` or
   * `/kpis` silently degrades to an empty table + zero-value KPI
   * tiles. The wrapper owns the copy (so it can attribute the
   * failure to the right endpoint or surface a retry hint).
   */
  errorMessage?: string;
};

const DEFAULT_LIMIT = 10;

const DEFAULT_LIMIT_OPTIONS: ReadonlyArray<TopUsersSelectOption<number>> = [
  { value: 10, label: "10 Users" },
  { value: 25, label: "25 Users" },
  { value: 50, label: "50 Users" },
  { value: 100, label: "100 Users" },
];

/**
 * Shared "Top Users" page used by every product (Sportsbook / Casino /
 * Poker / …). The component owns the page chrome — title, optional date
 * picker, optional view tab strip, filter row, KPI grid, and data table
 * — and is fully driven by props supplied by a thin per-product wrapper.
 *
 * The wrapper supplies the parts that genuinely differ per product
 * (which filter dropdowns to render, which KPI tiles, which table
 * columns, the data to fill them). The shared component owns the
 * *local* interaction state (date range, draft filter values, active
 * view) and surfaces it back through `onDatesChange` / `onViewChange` /
 * `onApply` so the caller can drive its data fetching.
 *
 * Each per-product route is gated by
 * `protectedElement(<ProductTopUsers />, { requiredPermission, developmentMode })`
 * in `App.js`; the gating is a wrapper concern, not a shared concern.
 */
const TopUsers = <TRow = Record<string, unknown>,>(
  props: TopUsersProps<TRow>,
) => {
  const {
    product,
    title = AppStrings.TOP_USERS,
    hideTitle = false,
    showDatePicker = false,
    initialDates,
    onDatesChange,
    views,
    activeView: controlledActiveView,
    defaultView,
    onViewChange,
    filters,
    limitOptions = DEFAULT_LIMIT_OPTIONS,
    defaultLimit = DEFAULT_LIMIT,
    onApply,
    onClear,
    showClear = false,
    kpis,
    kpisLoading,
    columns,
    rows,
    rowKey = "key",
    loading,
    tableClassName,
    rowClassName,
    errorMessage,
  } = props;

  const { setActiveSidebarTab } =
    (useContext(AppContext) as AppContextValue | null) ?? {};

  useEffect(() => {
    setActiveSidebarTab?.(product, "TOP_USERS");
  }, [setActiveSidebarTab, product]);

  // ─── Local state ───────────────────────────────────────────────────────────

  const [dates, setDates] = useState<[Dayjs, Dayjs]>(
    () => {
      if (initialDates) return initialDates;
      const now = getAdminNow();
      return [now.startOf("month"), now.endOf("day")];
    },
  );

  const [uncontrolledView, setUncontrolledView] = useState<string | undefined>(
    () => defaultView ?? views?.[0]?.id,
  );
  const activeView = controlledActiveView ?? uncontrolledView;

  // Draft → user is editing the dropdowns. Applied → committed on click.
  // The split mirrors `UtmReports.jsx` so the Apply button is the only
  // thing that triggers a refetch (via `onApply`).
  //
  // Multi-select filters seed to `[]` (not `undefined`) when no
  // `defaultValue` is provided so the value shape stays consistent
  // across "untouched", "cleared", and "selection in progress". Without
  // this, an untouched multi-select would surface `undefined` on the
  // first Apply and switch to `[]` after any interaction — forcing the
  // BE encoder to treat both as "no filter". Single-select keeps the
  // existing `undefined` semantics for "no value".
  //
  // Extracted into a helper so `handleClear` can re-seed drafts back to
  // their initial shape without duplicating the (slightly fiddly)
  // multi-vs-single seeding rules.
  const buildInitialDraftFilters =
    (): Record<string, TopUsersFilterValue> => {
      const seed: Record<string, TopUsersFilterValue> = {
        limit: defaultLimit,
      };
      filters?.forEach((f) => {
        if (f.defaultValue !== undefined) {
          seed[f.id] = f.defaultValue;
        } else if (f.mode === "multiple") {
          seed[f.id] = [];
        } else {
          seed[f.id] = undefined;
        }
      });
      return seed;
    };
  const [draftFilters, setDraftFilters] = useState<
    Record<string, TopUsersFilterValue>
  >(buildInitialDraftFilters);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleDatesChange = (next: [Dayjs, Dayjs]) => {
    setDates(next);
    onDatesChange?.(next);
  };

  const handleViewClick = (id: string) => {
    if (controlledActiveView === undefined) {
      setUncontrolledView(id);
    }
    onViewChange?.(id);
  };

  const handleApply = () => {
    onApply?.({
      dates,
      view: activeView,
      filters: { ...draftFilters },
    });
  };

  // Reset the drafts here and let the wrapper mirror that on its own
  // applied/committed filters via `onClear`. Same split as Apply —
  // the shared component owns the *draft* state; the wrapper owns the
  // *applied* state. Dates and the active view are intentionally not
  // touched (separate primary controls, not filters).
  const handleClear = () => {
    setDraftFilters(buildInitialDraftFilters());
    onClear?.();
  };

  // ─── Derived flags (drive what gets rendered) ──────────────────────────────

  // The toolbar is opt-in: a wrapper enables it by passing at least one of
  // `views` / `filters`. The limit select and the Apply button ride along
  // inside the same filter row, so they appear together with the rest of
  // the toolbar (and are hidden entirely on placeholder pages like
  // Casino / Poker today, which pass only `product`).
  const hasViews = Boolean(views && views.length > 0);
  const hasExtraFilters = Boolean(filters && filters.length > 0);
  const showToolbar = hasViews || hasExtraFilters;
  const showLimit =
    showToolbar && limitOptions !== null && limitOptions !== undefined;
  const hasKpis = Boolean(kpis && kpis.length > 0);
  const hasTable = Boolean(columns && columns.length > 0);

  return (
    <>
      <div className={styles.container} data-testid="top-users-page">
        <div
          className={`${styles.header} ${
            hideTitle ? styles.headerWithoutTitle : ""
          }`}
        >
          {!hideTitle && <h1 className={styles.title}>{title}</h1>}
          {showDatePicker && (
            <div className={styles.headerControls}>
              <DatePicker
                dates={dates}
                setDates={handleDatesChange}
                getData={() => {}}
                showPresets
                startRange={DATE_RANGE.CURRENT_MONTH}
              />
            </div>
          )}
        </div>

        {errorMessage && (
          <Alert
            type="error"
            showIcon
            message={errorMessage}
            data-testid="top-users-error"
            style={{ width: "100%" }}
          />
        )}

        {showToolbar && (
          <div className={styles.toolbar}>
            {hasViews && (
              <div
                className={styles.viewSelector}
                role="tablist"
                aria-label="Top Users view"
                data-testid="top-users-view-selector"
              >
                {views!.map(({ id, label }) => {
                  const isActive = id === activeView;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`${styles.viewSelectorTab} ${
                        isActive ? styles.viewSelectorTabActive : ""
                      }`.trim()}
                      data-testid={`top-users-view-selector-${id}`}
                      onClick={() => handleViewClick(id)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}

            <div className={styles.filterRow}>
              {showLimit && (
                <Select<number>
                  className={styles.filterSelect}
                  placeholder={`${defaultLimit} Users`}
                  value={draftFilters.limit as number | undefined}
                  onChange={(value) =>
                    setDraftFilters((prev) => ({ ...prev, limit: value }))
                  }
                  options={[...limitOptions!]}
                  data-testid="top-users-limit-select"
                />
              )}
              {filters?.map((f) =>
                f.mode === "multiple" ? (
                  // Multi-select: antd Select returns an array on change; we
                  // store it verbatim. The empty array represents the
                  // cleared state (allowClear semantics) and the BE encoder
                  // should map to `?<id>[in]=<v>,<v>`.
                  //
                  // antd's `SelectProps<ValueType>` does not conditionally
                  // widen `value` based on `mode`, so we parameterise the
                  // generic with the array type to keep value/onChange in
                  // sync at the TS layer.
                  <Select<(string | number)[]>
                    key={f.id}
                    className={styles.filterSelect}
                    placeholder={f.placeholder}
                    mode="multiple"
                    value={
                      draftFilters[f.id] as
                        | ReadonlyArray<string | number>
                        | undefined as (string | number)[] | undefined
                    }
                    onChange={(value) =>
                      setDraftFilters((prev) => ({ ...prev, [f.id]: value }))
                    }
                    options={[...f.options]}
                    allowClear={f.allowClear}
                    data-testid={f.testId ?? `top-users-${f.id}-select`}
                  />
                ) : (
                  // Single-select: scalar value, `undefined` is the cleared
                  // state (allowClear semantics) — BE encoder maps to
                  // `?<id>[eq]=<v>` (or omit for "all").
                  <Select<string | number>
                    key={f.id}
                    className={styles.filterSelect}
                    placeholder={f.placeholder}
                    value={draftFilters[f.id] as string | number | undefined}
                    onChange={(value) =>
                      setDraftFilters((prev) => ({ ...prev, [f.id]: value }))
                    }
                    options={[...f.options]}
                    allowClear={f.allowClear}
                    data-testid={f.testId ?? `top-users-${f.id}-select`}
                  />
                ),
              )}
              {showClear && (
                <Button
                  intent="neutral"
                  variant="outline"
                  size="large"
                  onClick={handleClear}
                  data-testid="top-users-clear-filters"
                >
                  {AppStrings.CLEAR_FILTERS}
                </Button>
              )}
              <Button
                intent="primary"
                size="large"
                onClick={handleApply}
                data-testid="top-users-apply"
              >
                Apply
              </Button>
            </div>
          </div>
        )}

        {hasKpis && (
          <div className={styles.kpisGrid}>
            {kpis!.map((kpi) => (
              <div key={kpi.key} className={styles.kpiCardWrapper}>
                <KpiCard
                  title={kpi.title}
                  titleTooltip={kpi.titleTooltip}
                  value={kpi.value}
                  percentage={kpi.percentage}
                  rate={kpi.rate}
                  maximumFractionDigits={kpi.maximumFractionDigits}
                  percentageMaximumFractionDigits={
                    kpi.percentageMaximumFractionDigits
                  }
                  backgroundColor={
                    kpi.backgroundColor ?? KPI_CARD_BACKGROUND.DARK
                  }
                  hidePercentage={kpi.hidePercentage}
                  isApiRunning={kpisLoading}
                  currencySymbol={kpi.currencySymbol}
                  currencySourceImage={kpi.currencySourceImage}
                />
              </div>
            ))}
          </div>
        )}

        {hasTable && (
          <div className={styles.tableSection}>
            <DataTable
              rowKey={rowKey}
              columns={[...columns!]}
              dataSource={(rows ? [...rows] : []) as TRow[]}
              pagination={false}
              loading={loading}
              className={tableClassName}
              rowClassName={rowClassName}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default TopUsers;
